import axios from 'axios';
import { API_BASE_URL } from '../api';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Start async tech pack generation. Server must return 202 immediately.
 * Keep this timeout short — if it hangs, fall back to kit polling.
 */
export const startTechPackGeneration = (data, token) => {
  const authToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
  return axios.post(`${API_BASE_URL}/tech-packs/generate`, data, {
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    timeout: 30000,
    // Accept 202 as success (axios does by default for 2xx)
    validateStatus: (status) => status >= 200 && status < 300,
  });
};

export const getTechPackJob = (jobId, token) => {
  const authToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
  return axios.get(`${API_BASE_URL}/tech-packs/jobs/${jobId}`, {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    timeout: 20000,
  });
};

export const getTechPackByKit = (kitId, token) => {
  const authToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
  return axios.get(`${API_BASE_URL}/tech-packs/by-kit/${kitId}`, {
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    timeout: 20000,
  });
};

async function pollUntilDone({ jobId, kitId, token, pollIntervalMs, maxWaitMs, onStatus }) {
  const deadline = Date.now() + maxWaitMs;
  let lastError = null;

  while (Date.now() < deadline) {
    await sleep(pollIntervalMs);
    try {
      const statusRes = jobId
        ? await getTechPackJob(jobId, token)
        : await getTechPackByKit(kitId, token);
      const job = statusRes?.data?.data;
      const status = job?.status;
      // If we started without jobId, pick it up from kit status
      if (!jobId && job?.jobId) {
        jobId = job.jobId;
      }
      onStatus?.({ status, jobId, error: job?.error });

      if (status === 'completed' && job?.techPack) {
        return {
          techPack: job.techPack,
          modelUsed: job.modelUsed,
          processingTime: job.processingTime,
          savedToKit: job.savedToKit !== false,
          jobId: job.jobId || jobId,
        };
      }

      if (status === 'failed') {
        throw new Error(job?.error || 'Tech pack generation failed');
      }
    } catch (error) {
      if (error?.response?.status === 404 && jobId) {
        // Job id unknown yet — fall back to kit polling
        jobId = null;
        lastError = error;
        continue;
      }
      if (String(error?.message || '').includes('Tech pack generation failed')) {
        throw error;
      }
      lastError = error;
      onStatus?.({ status: 'processing', jobId, warning: 'poll_retry' });
    }
  }

  throw new Error(
    lastError?.response?.data?.message ||
      lastError?.message ||
      'Tech pack is taking longer than expected. Please check again in a moment.'
  );
}

/**
 * Start generation and poll until the tech pack is ready.
 * If the start request times out (old server / hung socket), recover by
 * polling the kit's techPackGeneration field.
 */
export const generateTechPack = async (data, token, options = {}) => {
  const {
    pollIntervalMs = 3000,
    maxWaitMs = 8 * 60 * 1000,
    onStatus,
  } = options;

  const kitId = data?.kitId;
  let jobId = null;

  try {
    const startRes = await startTechPackGeneration(data, token);
    const startPayload = startRes?.data?.data;

    if (startPayload?.credits) {
      try {
        window.dispatchEvent(
          new CustomEvent('fashdeck:credits-updated', {
            detail: {
              imageCredits: startPayload.credits.remaining,
              deducted: startPayload.credits.deducted,
            },
          })
        );
      } catch (_) {
        // ignore if window unavailable
      }
    }

    // Legacy sync server still returning the finished pack
    if (startPayload?.techPack) {
      return {
        techPack: startPayload.techPack,
        modelUsed: startPayload.modelUsed,
        processingTime: startPayload.processingTime,
        savedToKit: startPayload.savedToKit,
        jobId: null,
        credits: startPayload.credits || null,
      };
    }

    jobId = startPayload?.jobId || null;
    if (jobId) {
      onStatus?.({ status: startPayload.status || 'queued', jobId });
    } else {
      onStatus?.({ status: 'queued', jobId: null });
    }
  } catch (error) {
    const timedOut =
      error?.code === 'ECONNABORTED' ||
      String(error?.message || '').toLowerCase().includes('timeout');
    if (!timedOut || !kitId) {
      throw error;
    }
    // Start hung (common with old sync backend). Work may still be running —
    // recover by polling the kit.
    console.warn('Tech pack start timed out; falling back to kit status polling');
    onStatus?.({ status: 'processing', jobId: null, warning: 'start_timeout_recovery' });
  }

  const result = await pollUntilDone({
    jobId,
    kitId,
    token,
    pollIntervalMs,
    maxWaitMs,
    onStatus,
  });

  return result;
};
